import { useTranslation } from "react-i18next";
import formatPrice from "@/core/helpers/formatPrice";
import { BucketIcon } from "@/shared/icons";
import Button from "@/shared/ui/button/Button";
import { CartItem } from "@/domains/shop/constants/cartItem";
import ShopCartItemsControls from "@/domains/shop/components/ShopCartItemsControls";

type Props = {
  item: CartItem;
  cartList: CartItem[];
  handleIncrement: (id: number) => void;
  handleDecrement: (id: number) => void;
  handleRemove: (id: number) => void;
};

export default function ShopCartCard({
  item,
  cartList,
  handleIncrement,
  handleDecrement,
  handleRemove,
}: Props) {
  const { t } = useTranslation("shop");
  const { id, title, price, image, weight, text } = item;

  const cartItem = cartList.find((el) => el.id === id);

  const total = (cartItem?.count ?? 0) * (price ?? 0) || 0;

  return (
    <div className="flex w-full h-[9.375rem] p-5 bg-dark-800 rounded-[0.75rem]">
      <div className="relative flex flex-col w-40 h-full mr-4 bg-[url(/svg/shop/dots.svg)] bg-center bg-no-repeat">
        <img
          src={image}
          alt="goods"
          className="absolute z-1 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        />
        <p className="mt-auto text-10 font-extrabold uppercase">
          <span className="text-11 font-bold text-accent-solid">{weight ?? 0}</span>{" "}
          {t("values.weight")}
        </p>
      </div>
      <div className="flex flex-col justify-center w-[24.125rem]">
        <p className="mb-2.5 text-16 font-medium">{title}</p>
        <p
          className="mb-4 h-9 text-15 font-medium opacity-60 overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </p>
        <p className="text-16 font-bold text-accent-solid">{formatPrice(price ?? 0)}₽</p>
      </div>
      <div className="flex flex-col gap-1.5 ml-auto">
        <div className="flex gap-1.5">
          <ShopCartItemsControls
            cartItem={cartItem}
            handleIncrement={handleIncrement}
            handleDecrement={handleDecrement}
          />
          <div className="w-10.5 h-10.5 p-1 bg-dark-800 rounded-xl">
            <Button
              className="group relative size-8.5 rounded-xl"
              icon
              onClick={() => handleRemove(id)}
              variant="soft"
              color="gray"
            >
              <span className="absolute size-full top-0 left-0 bg-gradient-accent rounded-xl opacity-0 transition-all ease-in-out duration-200 group-hover:opacity-100" />
              <BucketIcon className="relative z-1 size-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-col justify-center py-4 px-7.5 items-center border border-[#1F1F1F] rounded-2xl">
          <p className="text-15 font-bold">{formatPrice(total)}₽</p>
          <p className="text-15 font-medium opacity-60">{t("cart.goodsPrice")}</p>
        </div>
      </div>
    </div>
  );
}
